import React, { useEffect, useMemo, useState } from "react";

import { Box } from "@chakra-ui/react";
import { TDateRangeFilterTypes, getTodayAsEndDateFilterValue } from "utils";

import { DropDown } from "ui/dropdown";
import { FILTER_POSITION_YAXIS_OFFSET, Filter } from "ui/filter";
import {
  IDateRangeValues,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { Paragraph3 } from "ui/typography";

import {
  customDateRangeFilterBtnCss,
  dateFilterDropDownPopUpMenuContentCss,
  genericCustomDateRangeFilterCss,
} from "./styles";
import { IDateFilterProps, TDateFilterType } from "./types";

const filterOptions: TFilterOption[] = [
  {
    type: "date-range",
    title: "",
    key: "dateRange",
  },
];
const DROP_DOWN_WIDTH = 240;
export const DEFAULT_FILTER_TEXT = "Select a filter";
export const dateFilterValueToTextMap: Record<string, string> = {
  [DEFAULT_FILTER_TEXT]: DEFAULT_FILTER_TEXT,
  allTime: "All Time",
  today: "Today",
  yesterday: "Yesterday",
  last7Days: "Last 7 Days",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  yearToDate: "Year to Date",
  dateRange: "Custom Date Range",
};
export const NORMAL_DATE_FILTERS: TDateRangeFilterTypes[] = [
  "today",
  "yesterday",
  "last7Days",
  "thisMonth",
  "lastMonth",
  "yearToDate",
  "oldestToNewest",
  "newestToOldest",
];

export const DateFilter = (props: IDateFilterProps) => {
  const {
    options,
    onApplyFilter,
    isLoading,
    dateFilterValue,
    setDateFilterValue,
    dateFilterValueToTextMap,
    dropDownBtnContent,
    customFilterButtonCss,
    className,
    dropDownPopUpRef,
    customDateRangeFilterRef,
    hideFilterValue,
    hideFilterCaretIcon,
  } = props;

  const [filterValues, setFilterValues] = useState<TFilterValues>({
    dateRange: {
      startDate: null,
      endDate: null,
    },
  });
  const [filterButtonBoundingRect, setFilterButtonBoundingRect] =
    useState<DOMRect | null>(null);

  const handleFilterValueChange = (key: string, value: TFilterValue) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const showCustomDateRangeFilter = () => {
    if (
      !customDateRangeFilterRef.current ||
      !dropDownPopUpRef.current ||
      !dropDownPopUpRef.current.dropDownBtnRef.current
    ) {
      return;
    }
    dropDownPopUpRef.current.handleToggleDropdown();
    customDateRangeFilterRef.current.handleToggleFilter();
  };

  const handleResetCustomDateRangeFilter = () => {
    setFilterValues({
      dateRange: {
        startDate: null,
        endDate: null,
      },
    });
  };

  const handleFilterItemClick = (type: TDateFilterType, key: string) => {
    if (!dropDownPopUpRef.current) return;
    if (type === "regular") {
      setDateFilterValue({
        [key]: key,
      });
      dropDownPopUpRef.current.handleToggleDropdown();
      return;
    }
    showCustomDateRangeFilter();
  };

  const dropDownBtnValue = useMemo(() => {
    const [value] = Object.values(dateFilterValue);
    if (value.includes("startDate") || value.includes("endDate")) {
      return dateFilterValueToTextMap["dateRange"];
    }
    handleResetCustomDateRangeFilter();
    return dateFilterValueToTextMap[value];
  }, [dateFilterValue]);

  const isApplyCustomDateRangeDisabled = useMemo(() => {
    const [value] = Object.values(dateFilterValue);
    const [key] = Object.keys(dateFilterValue);
    if (key !== "dateRange") return true;
    return !value.includes("startDate");
  }, [dateFilterValue]);

  useEffect(() => {
    if (
      dropDownPopUpRef.current &&
      dropDownPopUpRef.current.dropDownBtnRef.current
    ) {
      const dropDownBtnBoundingRect =
        dropDownPopUpRef.current.dropDownBtnRef.current.getBoundingClientRect();

      setFilterButtonBoundingRect({
        ...dropDownBtnBoundingRect,
        top:
          dropDownBtnBoundingRect.top +
          FILTER_POSITION_YAXIS_OFFSET +
          dropDownBtnBoundingRect.height,
        left: dropDownBtnBoundingRect.left + dropDownBtnBoundingRect.width,
      });
    }
  }, [dropDownPopUpRef]);

  useEffect(() => {
    const { startDate, endDate } = filterValues.dateRange as IDateRangeValues;
    if (startDate && endDate) {
      setDateFilterValue({
        dateRange: `startDate=${startDate}&endDate=${endDate}`,
      });
    } else if (startDate) {
      setDateFilterValue({
        dateRange: `startDate=${startDate}&endDate=${getTodayAsEndDateFilterValue()}`,
      });
    }
  }, [filterValues]);

  return (
    <>
      <DropDown
        dropDownBtnValue={hideFilterValue ? "" : dropDownBtnValue}
        customDropDownButtonCss={customFilterButtonCss}
        className={className}
        dropDownPopUpMenuContentCss={dateFilterDropDownPopUpMenuContentCss}
        hasChevronDownIcon={!hideFilterCaretIcon}
        ref={dropDownPopUpRef}
        dropDownWidth={DROP_DOWN_WIDTH}
        dropDownBtnContent={dropDownBtnContent}
        allowOutSideClick
      >
        <>
          {options.map(({ title, key, type }) => {
            return (
              <Box
                className="h-10 py-[10px] px-4 flex items-center cursor-pointer"
                key={key}
                onClick={() => handleFilterItemClick(type, key)}
              >
                <Paragraph3
                  textColor="#1A202C"
                  lineHeight="20px"
                  fontWeight="500"
                >
                  {title}
                </Paragraph3>
              </Box>
            );
          })}
          <Box
            className="h-10 py-[10px] px-4 flex items-center cursor-pointer border-t border-[#DDDDDD]"
            onClick={() => handleFilterItemClick("regular", "allTime")}
          >
            <Paragraph3
              textColor="#00535C"
              lineHeight="20px"
              fontWeight="500"
            >
              Reset Filter
            </Paragraph3>
          </Box>
        </>
      </DropDown>
      <Filter
        options={filterOptions}
        values={filterValues}
        onChange={handleFilterValueChange}
        onApplyFilter={onApplyFilter}
        isLoading={isLoading}
        ref={customDateRangeFilterRef}
        filterBtnCss={customDateRangeFilterBtnCss}
        headerTitle="Custom Date Range"
        customFilterPopUpCss={genericCustomDateRangeFilterCss}
        customFilterBodyCss={genericCustomDateRangeFilterCss}
        filterButtonBoundingRect={filterButtonBoundingRect}
        isApplyBtnDisabled={isApplyCustomDateRangeDisabled}
        handleResetFilter={handleResetCustomDateRangeFilter}
      />
    </>
  );
};
